/**
 * Client-side helpers for Patient Queue definitions (Module 2 — see
 * healo_queue/QUEUE_SPECIFICATION.md §5/§6.1). The queue editor edits a queue definition draft
 * and persists it with one PUT/POST against /api/admin/queues (see src/lib/api/queues.js).
 *
 * Colours reuse the thread-groups palette so swimlane/sub-category chips render with the same
 * light/dark contrast as elsewhere in the app — no new palette is introduced.
 */

import {
  GROUP_COLOR_PALETTE,
  getGroupColor,
  nextUnusedColorId,
  slugifyGroupId,
} from "@/lib/thread-groups";
import { resolveTenantDisplayName } from "@/lib/tenant-display";

export { GROUP_COLOR_PALETTE, getGroupColor, nextUnusedColorId, slugifyGroupId };

const VALID_COLOR_IDS = new Set(GROUP_COLOR_PALETTE.map((c) => c.id));
const FALLBACK_COLOR_ID = "slate";

// The three transition-event triggers fired per sub-category (§6.5). Config-only: the keys are
// free-form strings; plugins subscribe to them in the runtime (built later).
export const EVENT_TRIGGERS = [
  { key: "onCheckIn", label: "On check-in", hint: "when first placed" },
  { key: "onEnter", label: "On enter", hint: "moved into this sub-category" },
  { key: "onExit", label: "On exit", hint: "moved out" },
];

// Patient-facing hero shapes a sub-status can declare for the patient app's live "My Visit" screen
// (Patient Queue Phase 1). Must stay in sync with queuePatientDisplayStyles in the Go backend.
export const PATIENT_DISPLAY_STYLES = [
  { id: "action", label: "Action card", hint: "icon + title + body (default)" },
  { id: "queueRing", label: "Queue ring", hint: "shows place in line (waiting)" },
  { id: "calmMinimal", label: "Calm / minimal", hint: "quiet reassurance card" },
];
const VALID_DISPLAY_STYLES = new Set(PATIENT_DISPLAY_STYLES.map((s) => s.id));
const FALLBACK_DISPLAY_STYLE = "action";

// Glyph slugs the patient app (AppIcons) can render for a patient-visible sub-status; "" = none.
export const PATIENT_ICONS = [
  "",
  "check",
  "users",
  "flask",
  "navigation",
  "stethoscope",
  "note",
  "mug",
  "map-pin",
  "bell",
  "timer",
  "eye",
  "glasses",
  "heart-pulse",
  "pill",
  "walk",
  "car",
  "sparkles",
  "forms",
  "info",
];

function clampDisplayStyle(id) {
  return VALID_DISPLAY_STYLES.has(id) ? id : FALLBACK_DISPLAY_STYLE;
}

// How a sub-category linked to another queue treats an arriving patient. Must stay in sync with
// QueueLinkMode* in the Go backend.
export const LINK_MODES = [
  { id: "transfer", label: "Move to queue", hint: "The tile physically moves to the target queue." },
  { id: "mirror", label: "Show in both", hint: "The tile stays here and a linked copy appears in the target queue." },
];

/**
 * Normalize a sub-category's linked-queue config: null unless a target queue id is set, mode
 * clamped to transfer|mirror, ids trimmed, and the mirror-only return placement emptied in
 * transfer mode. Mirrors normalizeSubCategoryLink on the backend.
 */
export function normalizeLinkedQueue(link) {
  const queueId = String(link?.queueId || "").trim();
  if (!queueId) return null;
  const mode = link?.mode === "mirror" ? "mirror" : "transfer";
  const mirror = mode === "mirror";
  return {
    queueId,
    mode,
    targetSwimlaneId: String(link?.targetSwimlaneId || "").trim(),
    targetSubCategoryId: String(link?.targetSubCategoryId || "").trim(),
    returnSwimlaneId: mirror ? String(link?.returnSwimlaneId || "").trim() : "",
    returnSubCategoryId: mirror ? String(link?.returnSubCategoryId || "").trim() : "",
  };
}

/**
 * Canonical in-clinic queue ordering (displayOrder ASC, then name) — mirrors
 * QueueDefinitionOrderLess on the backend; the first queue with direct check-in enabled is the
 * clinic's primary queue.
 */
export function sortInClinic(a, b) {
  return (
    (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0) ||
    String(a?.name || "").localeCompare(String(b?.name || ""), undefined, { sensitivity: "base" })
  );
}

// Seed entry types for a new queue (mirrors the design mockup). Per-queue, not global.
export const DEFAULT_ENTRY_TYPES = [
  { id: "consultation", name: "Consultation", showToDoctor: true },
  { id: "follow-up-review", name: "Follow-up review", showToDoctor: true },
  { id: "procedure-booking", name: "Procedure booking", showToDoctor: false },
  { id: "medication-pickup", name: "Medication pickup", showToDoctor: false },
  { id: "sign-visit-consent", name: "Sign visit consent", showToDoctor: false },
];

// Clinics are tenants — the attachment dropdown reuses the same source as the calendar/slots
// "Tenants / Location" filter (fetchTenants → resolveTenantDisplayName). clinicId stores the
// tenant ObjectId hex; "" = unattached.
export function tenantsToClinicOptions(items) {
  return (Array.isArray(items) ? items : [])
    .map((t) => {
      const id = String(t?.id || t?._id || "").trim();
      if (!id) return null;
      return { id, label: resolveTenantDisplayName(t, id) };
    })
    .filter(Boolean);
}

export function clinicLabel(clinicId, options) {
  const id = String(clinicId || "").trim();
  if (!id) return "";
  const found = (Array.isArray(options) ? options : []).find((o) => o.id === id);
  return found ? found.label : id;
}

function clampColorId(id) {
  return VALID_COLOR_IDS.has(id) ? id : FALLBACK_COLOR_ID;
}

/** Return a slug unique within `existing` (mirrors uniqueGroupId in thread-groups-editor). */
export function uniqueId(base, existing) {
  const taken = new Set(existing || []);
  if (!taken.has(base)) return base;
  for (let i = 2; i < 1000; i += 1) {
    const candidate = `${base}-${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}-${Date.now()}`;
}

export function makeEvents() {
  return { onCheckIn: [], onEnter: [], onExit: [] };
}

export function makeSubCategory(siblings = []) {
  const id = uniqueId(
    slugifyGroupId("New sub-category"),
    siblings.map((s) => s.id),
  );
  return {
    id,
    name: "New sub-category",
    description: "",
    colorId: nextUnusedColorId(siblings),
    order: siblings.length,
    events: makeEvents(),
    showToPatient: false,
    patientDisplayStyle: "action",
    patientIcon: "",
    room: "",
    roomLabel: "",
    showVisitSummary: false,
    showQueuePosition: true,
    notifyPatient: false,
    isDoctorWaitingStage: false,
    isConsultationStage: false,
    isPostVisitStage: false,
    isAwaitingTestsStage: false,
    linkedQueue: null,
  };
}

export function makeSwimlane(siblings = []) {
  const id = uniqueId(
    slugifyGroupId("New swimlane"),
    siblings.map((s) => s.id),
  );
  return {
    id,
    name: "New swimlane",
    description: "",
    colorId: nextUnusedColorId(siblings),
    order: siblings.length,
    optional: false,
    stackWithPrevious: false,
    subCategories: [],
  };
}

export function makeEntryType(siblings = []) {
  const id = uniqueId(
    slugifyGroupId("New entry type"),
    siblings.map((s) => s.id),
  );
  return {
    id,
    name: "New entry type",
    showToDoctor: true,
    order: siblings.length,
    defaultVisitMinutes: null,
  };
}

/** A single consultation mode row (e.g. "In person", "Video call"). Sibling array to swimlanes/entryTypes. */
export function makeConsultationMode(siblings = []) {
  const id = uniqueId(
    slugifyGroupId("New mode"),
    siblings.map((m) => m.id),
  );
  return {
    id,
    label: "New mode",
    requiresLink: false,
    order: siblings.length,
  };
}

// The built-in ophthalmology catalogue — kept in step with storage.DefaultQueueTests() on the
// backend, which is what a clinic gets until it configures its own. Offered here as a starting
// point so nobody has to retype 25 tests to change two of them.
export const DEFAULT_TEST_CATEGORIES = [
  { id: "vision-refraction", name: "Vision & refraction" },
  { id: "pressure", name: "Pressure" },
  { id: "visual-fields", name: "Visual fields" },
  { id: "imaging", name: "Imaging" },
  { id: "biometry-ultrasound", name: "Biometry & ultrasound" },
  { id: "dilation", name: "Dilation" },
];

export const DEFAULT_TESTS = [
  ["visual-acuity", "Visual Acuity", "vision-refraction", ""],
  ["refraction", "Refraction", "vision-refraction", ""],
  ["cycloplegic-refraction", "Cycloplegic Refraction", "vision-refraction", ""],
  ["arak", "ARAK", "vision-refraction", ""],
  ["dominant-eye-test", "Dominant Eye Test", "vision-refraction", ""],
  ["ishihara-color-vision", "Ishihara Color Vision", "vision-refraction", ""],
  ["amsler-grid", "Amsler Grid", "vision-refraction", ""],
  ["intraocular-pressure", "Intraocular Pressure", "pressure", ""],
  ["humphrey-24-2", "Humphrey 24-2", "visual-fields", ""],
  ["humphrey-24-2c", "Humphrey 24-2C", "visual-fields", ""],
  ["humphrey-ptosis", "Humphrey (Ptosis Protocol)", "visual-fields", ""],
  ["fundus-photography", "Fundus Photography", "imaging", ""],
  ["as-oct", "AS-OCT", "imaging", ""],
  ["oct-rnfl", "OCT RNFL", "imaging", ""],
  ["oct-macula", "OCT Macula", "imaging", ""],
  ["oct-rnfl-macula", "OCT RNFL + Macula", "imaging", ""],
  ["oct-a", "OCT-A", "imaging", ""],
  ["topography", "Topography", "imaging", ""],
  ["biometry", "Biometry", "biometry-ultrasound", ""],
  ["axial-length", "Axial Length", "biometry-ultrasound", ""],
  ["a-scan", "A-Scan", "biometry-ultrasound", ""],
  ["b-scan", "B-Scan", "biometry-ultrasound", ""],
  ["dilation-right", "Dilation (Right Eye)", "dilation", "right"],
  ["dilation-left", "Dilation (Left Eye)", "dilation", "left"],
  ["dilation-both", "Dilation (Both Eyes)", "dilation", "both"],
].map(([id, name, categoryId, dilationEye], order) => ({ id, name, categoryId, order, dilationEye }));

/** One orderable-test category ("Imaging"). Sibling array to swimlanes/entryTypes. */
export function makeTestCategory(siblings = []) {
  const id = uniqueId(
    slugifyGroupId("New category"),
    siblings.map((c) => c.id),
  );
  return { id, name: "New category", order: siblings.length };
}

/** One orderable test, filed under a category. */
export function makeQueueTest(categoryId, siblings = []) {
  const id = uniqueId(
    slugifyGroupId("New test"),
    siblings.map((t) => t.id),
  );
  return { id, name: "New test", categoryId, order: siblings.length, dilationEye: "" };
}

/** The three eyes a dilation test can name; "" = an ordinary test. */
export const DILATION_EYES = ["", "right", "left", "both"];

/** A fresh queue definition (POST body for "New queue") — a usable Waiting lane + seed types. */
export function makeQueue() {
  return {
    name: "New queue",
    description: "",
    active: true,
    clinicId: "",
    directCheckIn: true,
    displayOrder: 0,
    swimlanes: [
      {
        id: "waiting",
        name: "Waiting",
        description: "Checked in, not yet called",
        colorId: "slate",
        order: 0,
        optional: false,
        subCategories: [
          {
            id: "general",
            name: "General",
            description: "Standard wait",
            colorId: "slate",
            order: 0,
            events: makeEvents(),
            showToPatient: true,
            patientDisplayStyle: "queueRing",
            patientIcon: "users",
            room: "",
            roomLabel: "",
            showQueuePosition: true,
          },
        ],
      },
    ],
    entryTypes: DEFAULT_ENTRY_TYPES.map((t, i) => ({ ...t, order: i })),
    consultationModes: [],
    testCategories: [],
    tests: [],
  };
}

// "X (copy)" / "X (copy 3)" — duplicating a copy re-numbers instead of nesting suffixes.
const COPY_SUFFIX_RE = /\s*\(copy(?:\s+\d+)?\)$/i;
const QUEUE_NAME_MAX = 80; // matches the editor's name input

/** First "<base> (copy N)" variant not already taken by `existingNames` (case-insensitive). */
export function duplicateQueueName(name, existingNames = []) {
  const raw = String(name || "").trim() || "Untitled queue";
  const base = raw.replace(COPY_SUFFIX_RE, "").trim() || raw;
  const taken = new Set(
    (Array.isArray(existingNames) ? existingNames : []).map((n) =>
      String(n || "").trim().toLowerCase(),
    ),
  );
  // Keep the result inside the 80-char limit by trimming the base, never the suffix.
  const withSuffix = (suffix) => {
    const room = QUEUE_NAME_MAX - suffix.length;
    return `${base.length > room ? base.slice(0, Math.max(room, 0)).trim() : base}${suffix}`;
  };
  const first = withSuffix(" (copy)");
  if (!taken.has(first.toLowerCase())) return first;
  for (let i = 2; i < 1000; i += 1) {
    const candidate = withSuffix(` (copy ${i})`);
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return withSuffix(` (copy ${Date.now()})`);
}

/**
 * POST body for "Duplicate queue": the whole definition minus its identity — a fresh unique name
 * inside the same clinic and a displayOrder that lands the copy LAST in that clinic group, so a
 * duplicate can never displace the clinic's primary queue (the first direct-check-in one) and no
 * sibling needs renumbering. Sub-category queue links are copied as-is: they point at OTHER
 * queues (a self-link is stripped on save, so the source can't carry one).
 */
export function duplicateQueueDraft(queue, allQueues = []) {
  const list = Array.isArray(allQueues) ? allQueues : [];
  const clinicId = String(queue?.clinicId || "").trim();
  const group = list.filter((q) => String(q?.clinicId || "").trim() === clinicId);
  const lastOrder = group.reduce((max, q) => Math.max(max, Number(q?.displayOrder) || 0), -1);
  return normalizeQueueDraft({
    ...queue,
    name: duplicateQueueName(
      queue?.name,
      group.filter((q) => q?.id !== queue?.id).map((q) => q?.name),
    ),
    displayOrder: lastOrder + 1,
  });
}

export function summarizeQueue(q) {
  const swimlanes = Array.isArray(q?.swimlanes) ? q.swimlanes : [];
  const laneCount = swimlanes.length;
  const subCount = swimlanes.reduce(
    (n, lane) => n + (Array.isArray(lane?.subCategories) ? lane.subCategories.length : 0),
    0,
  );
  return { laneCount, subCount };
}

function cleanEventKeys(keys) {
  const seen = new Set();
  const out = [];
  for (const raw of Array.isArray(keys) ? keys : []) {
    const key = String(raw || "").trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/**
 * Produce the clean payload sent to the server — trim text, regenerate unique slug ids, clamp
 * colour ids to the palette, re-derive `order` from array position, and coerce event-key arrays.
 * Mirrors NormalizeQueueDefinition on the backend so the editor and server agree on the shape.
 */
export function normalizeQueueDraft(draft) {
  const laneIds = new Set();
  const swimlanes = (Array.isArray(draft?.swimlanes) ? draft.swimlanes : []).map((lane, i) => {
    const id = uniqueId(slugifyGroupId(lane?.id || lane?.name || "lane"), laneIds);
    laneIds.add(id);
    const subIds = new Set();
    const subCategories = (Array.isArray(lane?.subCategories) ? lane.subCategories : []).map(
      (sub, j) => {
        const sid = uniqueId(slugifyGroupId(sub?.id || sub?.name || "sub"), subIds);
        subIds.add(sid);
        return {
          id: sid,
          name: String(sub?.name || "").trim(),
          description: String(sub?.description || "").trim(),
          colorId: clampColorId(sub?.colorId),
          order: j,
          events: {
            onCheckIn: cleanEventKeys(sub?.events?.onCheckIn),
            onEnter: cleanEventKeys(sub?.events?.onEnter),
            onExit: cleanEventKeys(sub?.events?.onExit),
          },
          showToPatient: Boolean(sub?.showToPatient),
          patientDisplayStyle: clampDisplayStyle(sub?.patientDisplayStyle),
          patientIcon: String(sub?.patientIcon || "").trim(),
          room: String(sub?.room || "").trim(),
          roomLabel: String(sub?.roomLabel || "").trim(),
          showVisitSummary: Boolean(sub?.showVisitSummary),
          // Default true (show) — absent/undefined keeps the legacy "position shown" behaviour.
          showQueuePosition: sub?.showQueuePosition !== false,
          // Default false — an existing queue must never start pushing on its own; a status
          // notifies only once someone ticks it.
          notifyPatient: Boolean(sub?.notifyPatient),
          isDoctorWaitingStage: Boolean(sub?.isDoctorWaitingStage),
          isConsultationStage: Boolean(sub?.isConsultationStage),
          isPostVisitStage: Boolean(sub?.isPostVisitStage),
          isAwaitingTestsStage: Boolean(sub?.isAwaitingTestsStage),
          linkedQueue: normalizeLinkedQueue(sub?.linkedQueue),
        };
      },
    );
    return {
      id,
      name: String(lane?.name || "").trim(),
      description: String(lane?.description || "").trim(),
      colorId: clampColorId(lane?.colorId),
      order: i,
      optional: Boolean(lane?.optional),
      // The first lane can't stack under anything — sanitize the flag away so a reorder that
      // promotes a stacked lane to the top yields a clean definition.
      stackWithPrevious: i > 0 && Boolean(lane?.stackWithPrevious),
      subCategories,
    };
  });

  const typeIds = new Set();
  const entryTypes = (Array.isArray(draft?.entryTypes) ? draft.entryTypes : []).map((t, i) => {
    const id = uniqueId(slugifyGroupId(t?.id || t?.name || "type"), typeIds);
    typeIds.add(id);
    const minutes = Number(t?.defaultVisitMinutes);
    return {
      id,
      name: String(t?.name || "").trim(),
      showToDoctor: Boolean(t?.showToDoctor),
      order: i,
      defaultVisitMinutes: Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : null,
    };
  });

  const modeIds = new Set();
  const consultationModes = (Array.isArray(draft?.consultationModes) ? draft.consultationModes : []).map(
    (m, i) => {
      const id = uniqueId(slugifyGroupId(m?.id || m?.label || "mode"), modeIds);
      modeIds.add(id);
      return {
        id,
        label: String(m?.label || "").trim(),
        requiresLink: Boolean(m?.requiresLink),
        order: i,
      };
    },
  );

  // Pure passthrough — no editor UI edits call-staff reasons, but the backend PUT $sets the field
  // unconditionally, so omitting it here would WIPE server-configured reasons on every Save.
  const reasonIds = new Set();
  const callStaffReasons = (Array.isArray(draft?.callStaffReasons) ? draft.callStaffReasons : []).map(
    (r, i) => {
      const id = uniqueId(slugifyGroupId(r?.id || r?.label || "reason"), reasonIds);
      reasonIds.add(id);
      return {
        id,
        label: String(r?.label || "").trim(),
        order: i,
      };
    },
  );

  // The orderable-test catalogue behind the Doctor Queue's Order Tests sheet. Like
  // callStaffReasons above, this MUST round-trip even when no tab edited it — the backend PUT
  // $sets the field unconditionally, so omitting it would wipe a clinic's catalogue on every Save.
  const categoryIds = new Set();
  const testCategories = (Array.isArray(draft?.testCategories) ? draft.testCategories : []).map(
    (c, i) => {
      const id = uniqueId(slugifyGroupId(c?.id || c?.name || "category"), categoryIds);
      categoryIds.add(id);
      return { id, name: String(c?.name || "").trim(), order: i };
    },
  );
  const testIds = new Set();
  const tests = (Array.isArray(draft?.tests) ? draft.tests : []).map((t, i) => {
    const id = uniqueId(slugifyGroupId(t?.id || t?.name || "test"), testIds);
    testIds.add(id);
    const eye = String(t?.dilationEye || "").trim().toLowerCase();
    return {
      id,
      name: String(t?.name || "").trim(),
      categoryId: String(t?.categoryId || "").trim(),
      order: i,
      dilationEye: DILATION_EYES.includes(eye) ? eye : "",
    };
  });

  const displayOrderRaw = Number(draft?.displayOrder);
  return {
    name: String(draft?.name || "").trim(),
    description: String(draft?.description || "").trim(),
    active: Boolean(draft?.active),
    clinicId: String(draft?.clinicId || "").trim(),
    // Absent/null (legacy drafts) = enabled; only an explicit false turns direct check-in off.
    directCheckIn: draft?.directCheckIn !== false,
    // Must round-trip through editor Saves or they would reset the list-pane ordering.
    displayOrder:
      Number.isFinite(displayOrderRaw) && displayOrderRaw >= 0 ? Math.round(displayOrderRaw) : 0,
    swimlanes,
    entryTypes,
    callStaffReasons,
    consultationModes,
    testCategories,
    tests,
  };
}
